import { useCallback, useState } from 'react';
import { useWebSocket } from './useWebSocket';
import { playerConfig } from '../config';

export interface IncomingReaction {
  emoji: string;
  timestamp: number;
}

export interface UseReactionsReturn {
  /** Send a reaction emoji via WebSocket */
  sendReaction: (emoji: string) => void;
  /** Last incoming reaction from another viewer */
  incomingReaction: IncomingReaction | null;
  /** Whether the WebSocket is connected */
  connected: boolean;
}

/**
 * Hook for real-time emoji reactions via WebSocket (Req 1.16, 1.23, 1.24).
 *
 * - Connects to the reaction WebSocket endpoint
 * - Sends reaction events: `{ type: "reaction", emoji: "🔥" }`
 * - Receives broadcast reactions from other viewers
 */
export function useReactions(): UseReactionsReturn {
  const [incomingReaction, setIncomingReaction] = useState<IncomingReaction | null>(null);

  const url = buildWebSocketUrl(playerConfig.wsReactionUrl);

  const handleMessage = useCallback((data: unknown) => {
    if (isReactionMessage(data)) {
      setIncomingReaction({
        emoji: data.emoji,
        timestamp: Date.now(),
      });
    }
  }, []);

  const { connected, send } = useWebSocket({
    url,
    onMessage: handleMessage,
    autoConnect: !!playerConfig.wsReactionUrl,
  });

  const sendReaction = useCallback(
    (emoji: string) => {
      send({ type: 'reaction', emoji });
    },
    [send]
  );

  return { sendReaction, incomingReaction, connected };
}

function buildWebSocketUrl(baseUrl: string): string {
  if (!baseUrl) return '';
  const channelId = playerConfig.channelId;
  const token = playerConfig.wsToken;
  return `${baseUrl}?token=${encodeURIComponent(token)}&channelId=${encodeURIComponent(channelId)}`;
}

interface ReactionMessage {
  type: 'reaction';
  emoji: string;
}

function isReactionMessage(data: unknown): data is ReactionMessage {
  return (
    typeof data === 'object' &&
    data !== null &&
    (data as Record<string, unknown>).type === 'reaction' &&
    typeof (data as Record<string, unknown>).emoji === 'string'
  );
}
